import { default as _axios } from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import useUserStore from "../context/useUserStore";
import storage from "../utils/storage";
import { attemptRefresh, clearSession } from "./session";

const API_BASE_URL = "/api";

const commonHeaders = {
  Accept: "application/json",
};

const headers = { ...commonHeaders, "Content-Type": "application/json" };

const multipartHeaders = {
  ...commonHeaders,
  "Content-Type": "multipart/form-data",
};

/**
 * Encodes an object as a URL query string.
 * @param {object} params
 * @returns {string}
 */
const queryString = (params) => {
  const query = Object.keys(params)
    .filter((k) => params[k] !== undefined && params[k] !== null)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join("&");
  return query.length ? `?${query}` : "";
};

/**
 * useApi — Configured Axios instance with TCSB auth pipeline.
 *
 * Auth flow (CON-001 §2, BLU-004 §3.1):
 * - Bearer token injected from useUserStore on every request.
 * - withCredentials: true — httpOnly refresh cookie auto-sent.
 * - 401 interceptor: attempt silent refresh → retry → redirect to /login.
 *
 * @returns {{ get, post, put, patch, del, postFormData, getUserToken }}
 */
function useApi() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken } = useUserStore();

  const axios = _axios.create({
    baseURL: API_BASE_URL,
    timeout: 600_000,
    withCredentials: true, // httpOnly refresh cookie (CON-001 §2)
  });

  // ── Request interceptor: inject Bearer token ──
  axios.interceptors.request.use((request) => {
    const token = accessToken || storage.getItem(storage.ITEMS.jwt);
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }
    return request;
  });

  // ── Response interceptor: silent refresh on 401 ──
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        // Shared 401 recovery (session.js) — same path the SWR read fetcher
        // uses (DEF-015), so the two never drift.
        const newToken = await attemptRefresh();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        }

        // Refresh failed — clear session and redirect to login, carrying the
        // page the user was on so re-login can return them there (FE-5).
        clearSession();
        // Avoid redirect loops: don't stash /login or /change-password as the
        // return target.
        const path = location?.pathname ?? "/";
        const isAuthRoute = path === "/login" || path === "/change-password";
        if (!isAuthRoute) {
          const from = `${path}${location?.search ?? ""}`;
          navigate("/login", { replace: true, state: { from } });
        } else {
          navigate("/login", { replace: true });
        }
      }

      return Promise.reject(error.response);
    }
  );

  /**
   * GET request — returns response data. Compatible with SWR fetcher.
   * @param {string} url
   * @param {object} [params]
   */
  const get = (url, params = {}) =>
    axios
      .get(`${url}${queryString(params)}`, { headers })
      .then((res) => res.data);

  // Alias for SWR fetcher compatibility
  const fetch = get;

  /**
   * POST request.
   * @param {string} url
   * @param {object} data
   */
  const post = (url, data) =>
    axios({ method: "post", url, data, timeout: 600_000, headers }).then(
      (res) => res.data
    );

  /**
   * PUT request.
   * @param {string} url
   * @param {object} data
   */
  const put = (url, data) =>
    axios.put(url, data, { headers }).then((res) => res.data);

  /**
   * PATCH request.
   * @param {string} url
   * @param {object} data
   */
  const patch = (url, data) =>
    axios.patch(url, data, { headers }).then((res) => res.data);

  /**
   * DELETE request.
   * @param {string} url
   */
  const del = (url) =>
    axios.delete(url, { headers }).then((res) => res.data);

  /**
   * POST multipart/form-data (file uploads).
   * @param {string} url
   * @param {FormData} formData
   */
  const postFormData = (url, formData) =>
    axios.post(url, formData, { headers: multipartHeaders }).then((res) => res.data);

  /** Returns the current access token from storage. */
  const getUserToken = () =>
    accessToken || storage.getItem(storage.ITEMS.jwt);

  return { get, fetch, post, put, patch, del, postFormData, getUserToken };
}

export default useApi;
